/**
 * Agendas disponibles (administración, 14/10/2026; antes «Disponibilidad
 * Dres.», 8/10/2026): llamadas al servidor y reglas pequeñas de la página
 * (pages/AgendasDisponiblesPage.jsx).
 *
 * Qué días viene cada doctor: /api/disponibilidad-doctores
 * (backend/services/disponibilidad_doctores.py). Sus vacaciones, días libres,
 * bloqueos y patrones de bloqueo: /api/agendas-disponibles
 * (backend/services/agendas_disponibles.py); los bloqueos sueltos, con el
 * backend de bloqueos de siempre (/api/agendas/bloqueos-lote).
 * Aquí no se recalcula nada: se pinta lo que llega y se decide a qué agenda
 * lleva cada día.
 */
import { api } from './api';

const RAIZ = '/api/disponibilidad-doctores';
const RAIZ_AD = '/api/agendas-disponibles';

export async function listarProfesionales() {
  const r = await api.get(`${RAIZ}/profesionales`);
  return Array.isArray(r.data) ? r.data : [];
}

export async function cargarCalendario(profesional, anio) {
  const r = await api.get(`${RAIZ}/calendario`, { params: { profesional, anio } });
  return r.data;
}

/** consulta y quirofano: true (viene), false (no viene) o null (lo que diga
 *  el patrón). Los dos a null: el día vuelve al patrón. `horas` (27/10/2026):
 *  {consulta_hora_inicio, consulta_hora_fin, quirofano_hora_inicio,
 *  quirofano_hora_fin}, solo las que haya; sin ellas, todo el día. */
export async function cambiarDia(profesional, fecha, consulta, quirofano, horas = null) {
  const r = await api.put(`${RAIZ}/dia`, { profesional, fecha, consulta, quirofano, ...(horas || {}) });
  return r.data;
}

export async function crearPatron(datos) {
  const r = await api.post(`${RAIZ}/patrones`, datos);
  return r.data;
}

export async function editarPatron(id, datos) {
  const r = await api.put(`${RAIZ}/patrones/${id}`, datos);
  return r.data;
}

export async function desactivarPatron(id) {
  const r = await api.delete(`${RAIZ}/patrones/${id}`);
  return r.data;
}

// ---------------------------------------------------------------------------
// Vacaciones, días libres y bloqueos (14/10/2026)
// ---------------------------------------------------------------------------

/** Lo que bloquea las agendas del doctor ese año: {dias, ausencias,
 *  patrones, puede_editar}. Con algo que no sea un objeto, null. */
export async function cargarBloqueos(profesional, anio) {
  const r = await api.get(`${RAIZ_AD}/bloqueos`, { params: { profesional, anio } });
  const d = r.data;
  return d && typeof d === 'object' && !Array.isArray(d) ? d : null;
}

/**
 * Marca vacaciones o día libre (`tipo`) de `desde` a `hasta`, o los quita
 * (`tipo` null). Si esos días hay citas, el servidor contesta 409 con
 * {detail, citas, dias} y no marca nada: se pregunta y se repite con
 * `confirmar: true`. Las citas no se anulan nunca.
 */
export async function ponerAusencia({ profesional, desde, hasta, tipo, confirmar = false }) {
  const r = await api.put(`${RAIZ_AD}/ausencia`, {
    profesional, desde, hasta: hasta || null, tipo: tipo || null, confirmar,
  });
  return r.data;
}

/** Si el error es el «hay citas esos días» (de `ponerAusencia`, de un patrón
 *  de bloqueo, de `bloquearAgendas` o de `completarAusencia`): {detail,
 *  citas}. Un 409 sin `citas` es otra cosa («otra persona lo ha cambiado»). */
export function citasPorConfirmar(e) {
  const d = e?.response?.data;
  return e?.response?.status === 409 && d && Number.isFinite(d.citas) ? d : null;
}

/** Quita unas vacaciones o un día libre enteros, de hoy en adelante (lo
 *  pasado se queda como estaba). */
export async function quitarAusencia(grupoId) {
  const r = await api.delete(`${RAIZ_AD}/ausencias/${grupoId}`);
  return r.data;
}

/** «Completar el bloqueo»: a las agendas del doctor a las que les falta (una
 *  agenda dada de alta después), su bloqueo esos días. Con citas, 409 como
 *  al marcarlas. */
export async function completarAusencia(grupoId, { confirmar = false } = {}) {
  const r = await api.post(`${RAIZ_AD}/ausencias/${grupoId}/completar`, { confirmar });
  return r.data;
}

export async function crearPatronBloqueo(datos) {
  const r = await api.post(`${RAIZ_AD}/patrones`, datos);
  return r.data;
}

export async function editarPatronBloqueo(id, datos) {
  const r = await api.put(`${RAIZ_AD}/patrones/${id}`, datos);
  return r.data;
}

export async function quitarPatronBloqueo(id) {
  const r = await api.delete(`${RAIZ_AD}/patrones/${id}`);
  return r.data;
}

/** bloqueado false: quita ese día del patrón; true: vuelve al patrón. Con
 *  `agendaId`, solo en esa agenda (si no, en todas las del patrón). */
export async function cambiarDiaPatronBloqueo(id, fecha, bloqueado, { agendaId = null, confirmar = false } = {}) {
  const cuerpo = { fecha, bloqueado };
  if (agendaId) cuerpo.agenda_id = agendaId;
  if (confirmar) cuerpo.confirmar = true;
  const r = await api.put(`${RAIZ_AD}/patrones/${id}/dia`, cuerpo);
  return r.data;
}

/**
 * Bloquea días enteros (sin horas) o unas horas de esos días en esas agendas,
 * con el backend de bloqueos de siempre: así la agenda los respeta igual. Si
 * esos días ya hay citas, 409 con cuántas (`citasPorConfirmar`) y no bloquea
 * nada; con `confirmar` se bloquea igual. Las citas no se anulan.
 */
export async function bloquearAgendas({ agendaIds, fechas, motivo, start = null, end = null, confirmar = false }) {
  const cuerpo = { agenda_ids: agendaIds, fechas, motivo };
  if (start && end) Object.assign(cuerpo, { start, end });
  if (confirmar) cuerpo.confirmar = true;
  const r = await api.post('/api/agendas/bloqueos-lote', cuerpo);
  return r.data;
}

/**
 * Cómo está cada agenda un día, para «Todas» en la Agenda:
 * {agenda_id: {estado: 'disponible'|'bloqueada'|'vacaciones'|'dia_libre'|null,
 * motivo}}. Cualquier otra cosa (o un fallo) se toma por «no lo sé»: null.
 */
export async function estadoDeAgendasDelDia(fecha) {
  const r = await api.get(`${RAIZ_AD}/dia`, { params: { fecha } });
  const d = r?.data;
  return d && typeof d === 'object' && !Array.isArray(d) ? d : null;
}

// ---------------------------------------------------------------------------
// Colores y aspecto de cada día
// ---------------------------------------------------------------------------

/** Dos verdes que se distinguen bien (también sin ver bien los colores: uno
 *  claro y otro oscuro) y el rojo de los bloqueos, el de la Agenda. Nada de
 *  amarillo (en la agenda es «online») ni de azul (se confundía con una
 *  cirugía). Los mismos de index.css (.disp-*). */
export const VERDE_CONSULTA = '#4ade80';
export const VERDE_QUIROFANO = '#166534';
export const ROJO = '#b91c1c';
export const BICOLOR = `linear-gradient(135deg, ${VERDE_CONSULTA} 50%, ${VERDE_QUIROFANO} 50%)`;

/** 'ambas' | 'consulta' | 'quirofano' | null: solo la disponibilidad. */
export function estadoDelDia(dia) {
  if (!dia) return null;
  if (dia.consulta && dia.quirofano) return 'ambas';
  if (dia.consulta) return 'consulta';
  if (dia.quirofano) return 'quirofano';
  return null;
}

/** ¿Algo de ese día está cambiado a mano (no sigue el patrón)? */
export function esManual(dia) {
  return !!(dia && (dia.consulta_manual || dia.quirofano_manual));
}

export const TEXTO_ESTADO = {
  ambas: 'Consulta y quirófano',
  consulta: 'Consulta',
  quirofano: 'Quirófano',
};

export const TEXTO_AUSENCIA = { vacaciones: 'Vacaciones', dia_libre: 'Día libre' };

const TEXTO_TIPO = { consulta: 'consulta', quirofano: 'quirófano' };

// ---------------------------------------------------------------------------
// Las horas (27/10/2026): de cuánto a cuánto viene
// ---------------------------------------------------------------------------

/**
 * Lo que viene un día, con sus horas si las tiene: «consulta»,
 * «consulta 09:00–14:00 y quirófano», «quirófano 16:00–20:00»… o '' si no
 * viene. Con `patron`, lo que dirían solo los patrones (`*_patron` y
 * `*_horas_patron`: lo que deja «volver al patrón»). Las horas son
 * informativas: las da el servidor como texto (`consulta_horas`,
 * `quirofano_horas`) y aquí solo se leen.
 */
export function textoDisponibilidad(dia, patron = false) {
  if (!dia) return '';
  const sufijo = patron ? '_patron' : '';
  return ['consulta', 'quirofano']
    .filter((t) => dia[`${t}${sufijo}`])
    .map((t) => {
      const horas = dia[`${t}_horas${sufijo}`];
      return `${TEXTO_TIPO[t]}${horas ? ` ${horas}` : ''}`;
    })
    .join(' y ');
}

/** «09:00–14:00» → {inicio: '09:00', fin: '14:00'}; con dos tramos (dos
 *  patrones) o sin horas, null: los campos de hora del día solo se rellenan
 *  con un tramo. */
export function tramoUnico(texto) {
  const m = /^(\d{2}:\d{2})–(\d{2}:\d{2})$/.exec(texto || '');
  return m ? { inicio: m[1], fin: m[2] } : null;
}

/** ¿Unas horas de inicio y fin están mal? Las dos o ninguna (vacías = todo el
 *  día), y la de fin después de la de inicio: la misma regla que el servidor. */
export function horasMal(inicio, fin) {
  if (!inicio && !fin) return false;
  return !inicio || !fin || fin <= inicio;
}

/**
 * Cómo se pinta un día: lo que viene (`disp`, de /calendario) con lo que lo
 * bloquea encima (`bloq`, de /bloqueos). Cada mitad del día es un tipo —
 * arriba a la izquierda la consulta, abajo a la derecha el quirófano— y va en
 * su verde si viene, en ROJO si ese tipo está cerrado el día entero (o el
 * doctor está de vacaciones o libre) y sin color si no hay nada.
 *
 * Devuelve {estado, clase, texto, parcial}:
 *  - estado: 'consulta' | 'quirofano' | 'ambas' | 'vacaciones' | 'dia_libre' |
 *    'bloqueado' | 'consulta-bloqueado' | 'bloqueado-quirofano' | 'ninguna';
 *  - clase: la de index.css (disp-consulta, disp-quirofano, disp-ambas,
 *    disp-rojo, disp-consulta-rojo, disp-rojo-quirofano) o '';
 *  - texto: lo que se lee («consulta 09:00–14:00 y quirófano»: con las
 *    horas de cada tipo que viene, si las tiene);
 *  - parcial: hay algo cerrado que no es un tipo entero (unas horas, o una de
 *    varias agendas): se avisa con una raya roja, más suave.
 */
export function aspectoDelDia(disp, bloq) {
  const ausencia = bloq?.ausencia || null;
  const mitad = (tipo) => {
    if (ausencia || bloq?.[`${tipo}_bloqueada`]) return 'rojo';
    return disp?.[tipo] ? tipo : null;
  };
  const c = mitad('consulta');
  const q = mitad('quirofano');
  const parcial = !!bloq?.parcial;
  const horas = (tipo) => (disp?.[`${tipo}_horas`] ? ` ${disp[`${tipo}_horas`]}` : '');
  if (ausencia) {
    return { estado: ausencia, clase: 'disp-rojo', texto: TEXTO_AUSENCIA[ausencia], parcial };
  }
  if (c === 'rojo' && q === 'quirofano') {
    return { estado: 'bloqueado-quirofano', clase: 'disp-rojo-quirofano', texto: `consulta bloqueada y quirófano${horas('quirofano')}`, parcial };
  }
  if (c === 'consulta' && q === 'rojo') {
    return { estado: 'consulta-bloqueado', clase: 'disp-consulta-rojo', texto: `consulta${horas('consulta')} y quirófano bloqueado`, parcial };
  }
  if (c === 'rojo' || q === 'rojo') {
    return { estado: 'bloqueado', clase: 'disp-rojo', texto: 'bloqueado', parcial };
  }
  const solo = estadoDelDia({ consulta: c === 'consulta', quirofano: q === 'quirofano' });
  if (solo) {
    const texto = textoDisponibilidad({
      consulta: c === 'consulta', quirofano: q === 'quirofano',
      consulta_horas: disp?.consulta_horas, quirofano_horas: disp?.quirofano_horas,
    });
    return { estado: solo, clase: `disp-${solo}`, texto, parcial };
  }
  return { estado: 'ninguna', clase: '', texto: 'sin disponibilidad', parcial };
}

/**
 * Las agendas que abre un día: la de consulta si tiene consulta (o la tiene
 * bloqueada), la de quirófano si tiene quirófano, las dos si tiene las dos;
 * de vacaciones o libre, todas las suyas. Un día solo con unas horas
 * bloqueadas abre las agendas de esos bloqueos.
 */
export function agendasDelDia(dia, profesional, bloq = null) {
  if (!profesional) return [];
  const de = (tipo) => (profesional[tipo] || []).map((a) => a.id);
  const ids = [];
  const todo = !!bloq?.ausencia;
  if (todo || dia?.consulta || bloq?.consulta_bloqueada) ids.push(...de('consulta'));
  if (todo || dia?.quirofano || bloq?.quirofano_bloqueada) ids.push(...de('quirofano'));
  if (!ids.length && bloq?.bloqueos?.length) ids.push(...bloq.bloqueos.map((b) => b.agenda_id));
  return [...new Set(ids.filter(Boolean))];
}

/** La dirección de la Agenda en ese día con esas agendas (CitasPage la lee y
 *  la limpia; no cambia las agendas que cada uno tiene guardadas). */
export function enlaceAgenda(fecha, ids) {
  return `/citas?fecha=${fecha}&agendas=${ids.map(encodeURIComponent).join(',')}`;
}

export const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
export const INICIAL_DIA = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
export const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto',
  'septiembre', 'octubre', 'noviembre', 'diciembre'];

/** [1, 3] → «martes y jueves». */
export function textoDias(dias = []) {
  const nombres = [...new Set(dias)].sort((a, b) => a - b)
    .filter((d) => d >= 0 && d <= 6).map((d) => DIAS_SEMANA[d].toLowerCase());
  if (!nombres.length) return '—';
  if (nombres.length === 1) return nombres[0];
  return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
}

/** «2099-03-01» → «01/03/2099». */
export function textoFecha(iso) {
  const s = String(iso || '');
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : s;
}

/** «01/03/2099» o «01/03/2099 – 05/03/2099». */
export function textoTramo(desde, hasta) {
  return !hasta || desde === hasta ? textoFecha(desde) : `${textoFecha(desde)} – ${textoFecha(hasta)}`;
}

/** «día entero» o «15:00–20:00». */
export function textoHoras(b) {
  return !b || b.all_day !== false ? 'día entero' : `${b.start || '—'}–${b.end || '—'}`;
}

const pad2 = (n) => String(n).padStart(2, '0');

/** El último «hasta» de un patrón de bloqueo que empieza `desde`: 12 meses
 *  justos (la misma regla que el servidor, `fin_maximo_del_patron`). */
export function finMaximoDelPatron(desde) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(desde || '')) return '';
  const [a, m, d] = desde.split('-').map(Number);
  // Un año después menos un día; del 29/02, el 28/02 del año siguiente.
  const fin = m === 2 && d === 29 ? new Date(a + 1, 1, 28) : new Date(a + 1, m - 1, d - 1);
  return `${fin.getFullYear()}-${pad2(fin.getMonth() + 1)}-${pad2(fin.getDate())}`;
}

/** Las semanas de un mes, de lunes a domingo: [[null, null, '2099-01-01', …], …]. */
export function semanasDelMes(anio, mes) {
  const primero = new Date(anio, mes, 1);
  const hueco = (primero.getDay() + 6) % 7; // lunes = 0
  const ultimo = new Date(anio, mes + 1, 0).getDate();
  const celdas = Array(hueco).fill(null);
  for (let d = 1; d <= ultimo; d += 1) celdas.push(`${anio}-${pad2(mes + 1)}-${pad2(d)}`);
  while (celdas.length % 7) celdas.push(null);
  const semanas = [];
  for (let i = 0; i < celdas.length; i += 7) semanas.push(celdas.slice(i, i + 7));
  return semanas;
}
